/**
 * v4.34.13 — Shared navigation source of truth.
 *
 * Both `<Sidebar />` (default left-side shell) and `<TopNav />`
 * (alternative top-bar shell) render from this list. Adding a future
 * module is ONE entry here.
 *
 * Each entry shape:
 * {
 *   id:              stable identifier (also default testid suffix)
 *   to:              react-router path
 *   icon:            lucide-react component
 *   labelKey:        i18n key for visible label
 *   show:            (auth) => boolean — permission predicate
 *   category:        'primary' | 'functions' | 'help'
 *                    'primary'   → top-nav primary bar slot
 *                    'functions' → inside the Functions dropdown
 *                    'help'      → right-edge utility slot (top nav)
 *   topNavOrder:     number — display order in the primary bar
 *                    (ignored for `functions`/`help`)
 *   insightId?:      contextual help id (already wired in sidebar)
 *   highlight?:      legacy sidebar styling hint (preserved as-is)
 *   color?:          legacy sidebar accent colour (preserved as-is)
 * }
 *
 * IMPORTANT — permission `show` predicates accept an `auth` object
 * containing { user, isSuperAdmin, isClientAdmin, isLocationManager,
 * canManageLocations, hasPermission, surveysEnabled }. Both shells
 * inject the same object so the visibility contract is identical.
 *
 * The five primary top-nav slots (per approved scope):
 *   1. Operational Base   — Card Fleet Ops
 *   2. Card Fleet Ops     — Cards
 *   3. Locations
 *   4. Analytics
 *   5. (Functions dropdown trigger — rendered separately)
 *
 * Help & Insights occupies the right edge of the top nav.
 *
 * EVERYTHING ELSE in the sidebar falls into `category: 'functions'`
 * and surfaces inside the Functions dropdown when top-nav mode is
 * active. Adding a future module with `category: 'functions'` makes
 * it appear in the dropdown automatically.
 */
import {
    LayoutDashboard, CreditCard, Users, MapPin, BarChart3,
    DollarSign, FileText, Clock, Activity, Presentation,
    GitCompare, Shield, Link2, Gift, ClipboardList, Boxes,
    ScanSearch, Building2, Tag, Mail, Settings, Eye, Megaphone,
    Newspaper, Hotel, BookOpen, Archive, BedDouble, Car, Calculator, FlaskConical,
    Target, Coins, Gamepad2, Radio,
} from 'lucide-react';
import { fleetLivesInCommandCenter } from './vertical';

export const NAV_ITEMS = [
    // ─── PRIMARY — Top-nav primary bar slots ──────────────────────
    {
        id: 'card-fleet-ops',
        to: '/admin/card-fleet-ops',
        icon: Boxes,
        labelKey: 'nav.card_fleet_ops',
        shortLabelKey: 'nav.card_fleet_ops_short',
        // Card Fleet Ops lives INSIDE the Command Center whenever that Command
        // Center is the restaurant one — for a tenant that is its own vertical,
        // for a super admin it is the CURRENT Command Center scope. Every other
        // vertical keeps the standalone entry exactly as before.
        show: (a) => (a.isSuperAdmin() || a.isClientAdmin() || a.isLocationManager())
            && !fleetLivesInCommandCenter(a.user),
        category: 'primary',
        topNavOrder: 1,
        highlight: true,
        color: 'text-sky-400',
        insightId: 'nav.card-fleet-ops',
    },
    {
        id: 'cards',
        to: '/cards',
        icon: CreditCard,
        labelKey: 'cards.title',
        shortLabelKey: 'nav.cards',
        show: () => true,
        category: 'primary',
        topNavOrder: 2,
        insightId: 'nav.cards',
    },
    {
        id: 'locations',
        to: '/locations',
        icon: MapPin,
        labelKey: 'nav.locations',
        show: (a) => a.hasPermission('view_locations') || a.isPartner(),
        category: 'primary',
        topNavOrder: 3,
        insightId: 'nav.locations',
    },
    {
        id: 'analytics',
        to: '/analytics',
        icon: BarChart3,
        labelKey: 'nav.analytics',
        show: (a) => a.hasPermission('view_analytics'),
        category: 'primary',
        topNavOrder: 4,
        insightId: 'nav.analytics',
    },
    // ─── FUNCTIONS — In dropdown when top-nav mode is active ──────
    {
        id: 'dashboard',
        to: '/dashboard',
        icon: LayoutDashboard,
        labelKey: 'nav.dashboard',
        show: () => true,
        category: 'functions',
        insightId: 'nav.dashboard',
    },
    {
        id: 'employees',
        to: '/employees',
        icon: Users,
        labelKey: 'nav.employees',
        show: (a) => a.hasPermission('view_employees'),
        category: 'functions',
        insightId: 'nav.employees',
    },
    {
        id: 'surveys',
        to: '/surveys',
        icon: ClipboardList,
        labelKey: 'nav.surveys',
        show: (a) => a.surveysEnabled && (a.isSuperAdmin() || a.isClientAdmin()),
        category: 'functions',
        insightId: 'nav.surveys',
    },
    {
        id: 'neo-status',
        to: '/neo-status',
        icon: MapPin,
        labelKey: 'nav.neoStatus',
        show: (a) => a.isSuperAdmin() || a.isClientAdmin(),
        category: 'functions',
        highlight: true,
        color: 'text-fuchsia-600',
        insightId: 'nav.neo-status',
    },
    {
        id: 'roi',
        to: '/roi',
        icon: DollarSign,
        labelKey: 'nav.roiDashboard',
        show: (a) => a.hasPermission('view_roi_dashboard'),
        category: 'functions',
        highlight: true,
        color: 'text-emerald-600',
    },
    {
        id: 'sales-report',
        to: '/sales-report',
        icon: FileText,
        labelKey: 'nav.salesReport',
        show: (a) => a.hasPermission('view_sales_report'),
        category: 'functions',
        highlight: true,
        color: 'text-purple-600',
    },
    {
        id: 'sales-calculation-tool',
        to: '/sales-calculation-tool',
        icon: Calculator,
        labelKey: 'nav.salesCalculationTool',
        show: (a) => a.isSuperAdmin() || a.isClientAdmin(),
        category: 'functions',
        highlight: true,
        color: 'text-emerald-600',
    },
    {
        id: 'shift-insights',
        to: '/shift-insights',
        icon: Clock,
        labelKey: 'nav.shiftInsights',
        show: (a) => a.hasPermission('view_shift_insights'),
        category: 'functions',
        highlight: true,
        color: 'text-blue-600',
    },
    {
        id: 'location-health',
        to: '/location-health',
        icon: Activity,
        labelKey: 'nav.locationHealth',
        show: (a) => a.hasPermission('view_location_health'),
        category: 'functions',
        highlight: true,
        color: 'text-emerald-600',
    },
    {
        id: 'command-center',
        to: '/command-center',
        icon: LayoutDashboard,
        labelKey: 'nav.commandCenter',
        // The gradient button is the entry in every shell that renders one;
        // this list entry stays as the fallback for any shell that does not.
        show: (a) => a.hasPermission('view_command_center') && !a.commandCenterButtonVisible,
        category: 'functions',
        highlight: true,
        color: 'text-emerald-500',
    },
    {
        // The loyalty program's way in. It lives inside the Command Center, so
        // this entry opens that screen on the panel for the organization that
        // screen is already scoped to.
        id: 'vip-rewards',
        to: '/command-center?panel=vip-rewards',
        icon: Gift,
        labelKey: 'loyalty.nav.vipRewards',
        show: (a) => a.hasPermission('loyalty_program'),
        category: 'functions',
        highlight: true,
        color: 'text-fuchsia-500',
    },
    {
        id: 'executive-summary',
        to: '/executive-summary',
        icon: Presentation,
        labelKey: 'nav.executiveSummary',
        show: (a) => a.hasPermission('view_executive_summary'),
        category: 'functions',
        highlight: true,
        color: 'text-purple-600',
    },
    {
        id: 'brand-comparison',
        to: '/brand-comparison',
        icon: GitCompare,
        labelKey: 'nav.brandComparison',
        show: (a) => a.hasPermission('view_brand_comparison'),
        category: 'functions',
        highlight: true,
        color: 'text-indigo-600',
    },
    {
        id: 'url-registry',
        to: '/url-registry',
        icon: Link2,
        labelKey: 'nav.urlRegistry',
        show: (a) => a.hasPermission('view_url_registry'),
        category: 'functions',
        highlight: true,
        color: 'text-cyan-600',
    },
    {
        id: 'review-shield',
        to: '/review-shield-results',
        icon: Shield,
        labelKey: 'nav.reviewShield',
        show: (a) => a.hasPermission('view_review_shield_results'),
        category: 'functions',
        highlight: true,
        color: 'text-emerald-600',
        insightId: 'nav.review-shield-results',
    },
    // ─── Hotel — August 2026 additions ──────────────────────
    {
        id: 'hotel-results',
        to: '/hotel-results',
        icon: Hotel,
        labelKey: 'nav.hotels',
        show: (a) => a.isSuperAdmin() || a.isClientAdmin(),
        category: 'functions',
        highlight: true,
        color: 'text-blue-600',
    },
    {
        id: 'hotel-rooms',
        to: '/hotel-rooms',
        icon: BedDouble,
        labelKey: 'nav.hotelRooms',
        show: (a) => (a.isSuperAdmin() || a.isClientAdmin()) && a.activeVertical !== 'automotive',
        category: 'functions',
        highlight: true,
        color: 'text-blue-600',
    },
    {
        id: 'used-vehicles',
        to: '/used-vehicles',
        icon: Car,
        labelKey: 'nav.usedVehicles',
        show: (a) => (a.isSuperAdmin() && a.activeVertical === 'automotive') || (a.isClientAdmin() && a.activeVertical === 'automotive') || (a.isLocationManager && a.isLocationManager() && a.activeVertical === 'automotive'),
        category: 'functions',
        highlight: true,
        color: 'text-amber-600',
    },
    {
        id: 'vehicle-leads',
        to: '/vehicle-leads',
        icon: Car,
        labelKey: 'nav.vehicleLeads',
        show: (a) => (a.isSuperAdmin() || a.isClientAdmin() || (a.isLocationManager && a.isLocationManager())) && a.activeVertical === 'automotive',
        category: 'functions',
        color: 'text-amber-600',
    },
    {
        id: 'hotel-ops',
        to: '/hotel-ops',
        icon: Building2,
        labelKey: 'nav.hotelOps',
        show: (a) => a.isSuperAdmin() || a.isClientAdmin(),
        category: 'functions',
        highlight: true,
        color: 'text-blue-600',
    },
    {
        id: 'hotel-setup',
        to: '/hotel-setup',
        icon: Hotel,
        labelKey: 'nav.hotelSetup',
        show: (a) => a.isSuperAdmin() || a.isClientAdmin(),
        category: 'functions',
        highlight: true,
        color: 'text-blue-600',
    },
    {
        id: 'hotel-manual',
        to: '/hotel-manual',
        icon: BookOpen,
        labelKey: 'nav.hotelManual',
        show: (a) => a.isSuperAdmin() || a.isClientAdmin(),
        category: 'functions',
        highlight: true,
        color: 'text-blue-600',
    },
    {
        id: 'card-guide',
        to: '/card-guide',
        icon: BookOpen,
        labelKey: 'nav.cardGuide',
        show: (a) => a.isSuperAdmin() || a.isClientAdmin() || a.isLocationManager(),
        category: 'functions',
        highlight: true,
        color: 'text-amber-600',
    },
    {
        id: 'voucher-settings',
        to: '/voucher-settings',
        icon: Gift,
        labelKey: 'nav.vouchers',
        show: (a) => a.hasPermission('manage_vouchers'),
        category: 'functions',
        highlight: true,
        color: 'text-amber-500',
        insightId: 'nav.voucher-settings',
    },
    {
        id: 'multi-location-links',
        to: '/multi-location-links',
        icon: Link2,
        labelKey: 'nav.multiLocationLinks',
        show: (a) => a.hasPermission('manage_multi_location_links'),
        category: 'functions',
        highlight: true,
        color: 'text-sky-500',
        insightId: 'nav.multi-location-links',
    },
    {
        id: 'promos',
        to: '/promos',
        icon: Megaphone,
        labelKey: 'nav.promos',
        show: (a) => a.hasPermission('promo_buttons'),
        category: 'functions',
        highlight: true,
        color: 'text-fuchsia-500',
        insightId: 'nav.promos',
    },
    {
        id: 'voucher-reports',
        to: '/voucher-reports',
        icon: Gift,
        labelKey: 'nav.vouchersLedger',
        show: (a) => a.hasPermission('view_voucher_reports'),
        category: 'functions',
        highlight: true,
        color: 'text-amber-500',
        insightId: 'nav.voucher-reports',
    },
    {
        id: 'redemption-investigation',
        to: '/redemption-investigation',
        icon: ScanSearch,
        labelKey: 'nav.redemptionInvestigation',
        show: (a) => a.isSuperAdmin() || a.user?.role === 'client_admin',
        // Sidebar historically rendered this inside the admin
        // collapsible (super_admin only). Top-nav Functions dropdown
        // surfaces both 'admin' and 'functions' entries, so client
        // admins gain reach via top-nav mode without the sidebar
        // visual changing.
        category: 'admin',
        highlight: true,
        color: 'text-rose-600',
        insightId: 'nav.redemption-investigation',
    },
    {
        id: 'management-audit',
        to: '/management/audit',
        icon: Shield,
        labelKey: 'nav.managementAudit',
        show: (a) => a.hasPermission('view_management_audit'),
        category: 'functions',
        highlight: true,
        color: 'text-blue-600',
        insightId: 'nav.management-audit',
    },
    {
        id: 'manager-access',
        to: '/manager-access',
        icon: Shield,
        labelKey: 'nav.managerAccess',
        show: (a) =>
            a.isSuperAdmin() ||
            a.user?.role === 'client_admin' ||
            a.hasPermission('manage_manager_access'),
        category: 'functions',
        highlight: true,
        color: 'text-fuchsia-500',
        insightId: 'nav.manager-access',
    },
    {
        id: 'test-data-cleanup',
        to: '/test-data-cleanup',
        icon: Archive,
        labelKey: 'nav.testDataCleanup',
        show: (a) => a.isSuperAdmin(),
        category: 'functions',
        color: 'text-orange-500',
        insightId: 'nav.test-data-cleanup',
    },
    // ─── ADMIN — Sidebar's "Admin" collapsible block + Functions dropdown ─
    // Goal Game pages: each one shows for a super admin, or for a staff member
    // whose own tick is on in the permissions screen's "Goal Game" group.
    {
        id: 'goal-game',
        to: '/goal-game',
        icon: Target,
        labelKey: 'nav.goalGame',
        show: (a) => a.hasPermission('manage_goal_game'),
        category: 'admin',
    },
    {
        id: 'live-game-view',
        to: '/live-game-view',
        icon: Radio,
        labelKey: 'nav.liveGameView',
        show: (a) => a.hasPermission('view_live_game'),
        category: 'admin',
    },
    {
        id: 'game-results',
        to: '/game-results',
        icon: Gamepad2,
        labelKey: 'nav.gameResults',
        show: (a) => a.hasPermission('view_game_results'),
        category: 'admin',
    },
    {
        id: 'patron-points',
        to: '/patron-points',
        icon: Coins,
        labelKey: 'nav.patronPoints',
        show: (a) => a.hasPermission('manage_patron_points'),
        category: 'admin',
    },
    {
        id: 'admin-organizations',
        to: '/admin/clients',
        icon: Building2,
        labelKey: 'nav.organizations',
        // A partner reads every organization; the page hides its own write controls.
        show: (a) => a.hasPermission('manage_organizations') || a.isPartner(),
        category: 'admin',
    },
    {
        id: 'admin-brands',
        to: '/admin/brands',
        icon: Tag,
        labelKey: 'nav.brands',
        show: (a) => a.hasPermission('manage_brands') || a.isPartner(),
        category: 'admin',
    },
    {
        id: 'admin-notifications',
        to: '/admin/notifications',
        icon: Mail,
        labelKey: 'nav.employeeEmails',
        show: (a) => a.hasPermission('manage_notifications'),
        category: 'admin',
    },
    {
        id: 'admin-platform-values',
        to: '/admin/platform-values',
        icon: DollarSign,
        labelKey: 'nav.platformValues',
        show: (a) => a.hasPermission('manage_platform_values'),
        category: 'admin',
    },
    {
        id: 'admin-users',
        to: '/admin/users',
        icon: Users,
        labelKey: 'nav.users',
        show: (a) => a.isSuperAdmin() || a.isPartner(),
        category: 'admin',
    },
    {
        id: 'admin-settings',
        to: '/admin/settings',
        icon: Settings,
        labelKey: 'nav.settings',
        show: (a) => a.isSuperAdmin(),
        category: 'admin',
    },
    {
        id: 'admin-user-welcome-dispatch',
        to: '/admin/user-welcome-dispatch',
        icon: Mail,
        labelKey: 'nav.userWelcomeDispatch',
        show: (a) => a.isSuperAdmin(),
        category: 'admin',
    },
    {
        id: 'admin-diagnostics',
        to: '/admin/diagnostics',
        icon: Activity,
        labelKey: 'nav.diagnostics',
        show: (a) => a.isSuperAdmin(),
        category: 'admin',
    },
    {
        id: 'admin-tap-health',
        to: '/admin/tap-health',
        icon: Activity,
        labelKey: 'nav.tapHealth',
        show: (a) => a.isSuperAdmin(),
        category: 'admin',
    },
    {
        id: 'admin-reconciliation',
        to: '/admin/reconciliation',
        icon: Activity,
        labelKey: 'nav.reconciliation',
        show: (a) => a.isSuperAdmin(),
        category: 'admin',
    },
    {
        id: 'admin-audit-logs',
        to: '/admin/audit-logs',
        icon: Shield,
        labelKey: 'nav.auditLogs',
        show: (a) => a.isSuperAdmin(),
        category: 'admin',
        insightId: 'nav.audit-logs',
    },
    {
        id: 'admin-permissions',
        to: '/admin/permissions',
        icon: Shield,
        labelKey: 'nav.permissions',
        show: (a) => a.isSuperAdmin(),
        category: 'admin',
    },
    {
        id: 'admin-broadcasts',
        to: '/admin/broadcasts',
        icon: Megaphone,
        labelKey: 'nav.broadcasts',
        show: (a) => a.isSuperAdmin(),
        category: 'admin',
        insightId: 'nav.broadcasts',
    },
    {
        id: 'timeline',
        to: '/timeline',
        icon: Activity,
        labelKey: 'nav.timeline',
        show: (a) => !!a.user,
        category: 'general',
        insightId: 'nav.timeline',
    },
    {
        id: 'weekly-report',
        to: '/weekly-report',
        icon: Newspaper,
        labelKey: 'nav.weeklyReport',
        show: (a) => a.isSuperAdmin() || a.isClientAdmin(),
        category: 'functions',
        highlight: true,
        color: 'text-amber-300',
    },
    {
        id: 'admin-insight-observation',
        to: '/admin/insight-observation',
        icon: Eye,
        labelKey: 'nav.insightObservation',
        show: (a) => a.isSuperAdmin(),
        category: 'admin',
    },
    {
        // TEMPORARY — Phase 4-0 wireframes, throwaway. Super admin ONLY, and
        // hidden the moment a non-grocery vertical is the active one. DELETE
        // this entry (and the route) the day the owner approves the look.
        id: 'grocery-insights-wireframes',
        to: '/insights-wireframes',
        icon: FlaskConical,
        labelKey: 'nav.shoppingInsightsSample',
        show: (a) => a.isSuperAdmin()
            && (!a.activeVertical || a.activeVertical === 'grocery'
                || a.activeVertical === 'restaurant'),
        category: 'functions',
        highlight: true,
        color: 'text-emerald-500',
    },
    {
        id: 'organization-settings',
        to: '/organization/settings',
        icon: Building2,
        labelKey: 'nav.organizationSettings',
        show: (a) => a.isClientAdmin(),
        category: 'functions',
    },
];

/**
 * Filter the list down to entries the current user can see, sorted
 * by category + topNavOrder.
 */
export function getVisibleNavItems(authContext) {
    return NAV_ITEMS.filter((item) => {
        try { return !!item.show(authContext); }
        catch { return false; }
    });
}

export function getPrimaryTopNavItems(authContext) {
    return getVisibleNavItems(authContext)
        .filter((i) => i.category === 'primary')
        .sort((a, b) => (a.topNavOrder ?? 999) - (b.topNavOrder ?? 999));
}

export function getFunctionsItems(authContext) {
    // Functions dropdown surfaces BOTH `functions` and `admin` entries.
    return getVisibleNavItems(authContext)
        .filter((i) => i.category === 'functions' || i.category === 'admin');
}

export function getAdminItems(authContext) {
    return getVisibleNavItems(authContext)
        .filter((i) => i.category === 'admin');
}

export function getSidebarMainItems(authContext) {
    // Sidebar historically showed primary + functions (NOT admin).
    return getVisibleNavItems(authContext)
        .filter((i) => i.category === 'primary' || i.category === 'functions');
}

